import {makeAutoObservable} from "mobx";
import nameConfigService from "../services/nameConfigService";
import {lc_name_cfg} from "../static/lc_name_cfg";

const clone = (value) => JSON.parse(JSON.stringify(value));

const normalizeLookup = (value) => (value || "")
  .normalize("NFKC")
  .normalize("NFKD")
  .replace(/[\u0300-\u036f]/g, "")
  .replace(/\s+/g, " ")
  .trim()
  .toLowerCase();

const normalizeDisplay = (value) => (value || "")
  .normalize("NFKC")
  .replace(/\s+/g, " ")
  .trim();

const toBooleanLookupMap = (source = {}) => Object.entries(source || {}).reduce((acc, [key, enabled]) => {
  if (enabled !== false) {
    const normalized = normalizeLookup(key);
    if (normalized) {
      acc[normalized] = true;
    }
  }
  return acc;
}, {});

const sanitizeDictionaryGroup = (group = {}, includeEndings = false) => ({
  givenNames: toBooleanLookupMap(group.givenNames),
  surnames: toBooleanLookupMap(group.surnames),
  ...(includeEndings ? {surnameEndings: toBooleanLookupMap(group.surnameEndings)} : {}),
});

const sanitizeNameConfig = (value = {}) => ({
  version: Number(value?.version || lc_name_cfg.version || 1),
  updatedAt: value?.updatedAt || null,
  updatedBy: value?.updatedBy || null,
  enabled: value?.enabled !== false,
  rules: {
    useDiacriticsBoost: value?.rules?.useDiacriticsBoost !== false,
    useSurnameEndings: value?.rules?.useSurnameEndings !== false,
    minLithuanianScore: Number(value?.rules?.minLithuanianScore ?? lc_name_cfg.rules.minLithuanianScore ?? 2),
    diacriticsScore: Number(value?.rules?.diacriticsScore ?? lc_name_cfg.rules.diacriticsScore ?? 4),
    ltGivenNameScore: Number(value?.rules?.ltGivenNameScore ?? lc_name_cfg.rules.ltGivenNameScore ?? 2),
    ltSurnameScore: Number(value?.rules?.ltSurnameScore ?? lc_name_cfg.rules.ltSurnameScore ?? 2),
    ltSurnameEndingScore: Number(value?.rules?.ltSurnameEndingScore ?? lc_name_cfg.rules.ltSurnameEndingScore ?? 2),
    nonLtGivenNameScore: Number(value?.rules?.nonLtGivenNameScore ?? lc_name_cfg.rules.nonLtGivenNameScore ?? -2),
    nonLtSurnameScore: Number(value?.rules?.nonLtSurnameScore ?? lc_name_cfg.rules.nonLtSurnameScore ?? -2),
    foreignDiacriticsScore: Number(value?.rules?.foreignDiacriticsScore ?? lc_name_cfg.rules.foreignDiacriticsScore ?? -2),
    foreignEndingScore: Number(value?.rules?.foreignEndingScore ?? lc_name_cfg.rules.foreignEndingScore ?? -2),
    tokenCountBoostScore: Number(value?.rules?.tokenCountBoostScore ?? lc_name_cfg.rules.tokenCountBoostScore ?? 1),
  },
  dictionaries: {
    lt: sanitizeDictionaryGroup({
      ...clone(lc_name_cfg.dictionaries.lt),
      ...(value?.dictionaries?.lt || {}),
    }, true),
    nonLt: sanitizeDictionaryGroup({
      ...clone(lc_name_cfg.dictionaries.nonLt),
      ...(value?.dictionaries?.nonLt || {}),
    }),
  },
});

const normalizeReviewEntries = (entries = []) => [...entries].sort((a, b) => {
  const aTime = new Date(a.lastSeenAt || a.updatedAt || a.firstSeenAt || 0).getTime();
  const bTime = new Date(b.lastSeenAt || b.updatedAt || b.firstSeenAt || 0).getTime();
  return bTime - aTime;
});

const normalizeTokenList = (text, fallback = []) => {
  const source = Array.isArray(fallback) && fallback.length > 0
    ? fallback
    : normalizeDisplay(text).split(/\s+/);
  return source
    .map((token) => normalizeLookup(token))
    .filter(Boolean);
};

export class NameConfigState {
  rootStore;
  config = {lc_name_cfg: clone(lc_name_cfg)};
  reviewQueue = [];
  dataLoaded = false;
  reviewLoaded = false;
  saving = false;
  reviewSavingIds = [];

  constructor(rootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this);
  }

  setConfig(config) {
    const next = config?.lc_name_cfg ? config.lc_name_cfg : config;
    this.config = {lc_name_cfg: sanitizeNameConfig(next)};
  }

  get activeConfig() {
    return this.config?.lc_name_cfg || sanitizeNameConfig(lc_name_cfg);
  }

  setReviewQueue(entries) {
    this.reviewQueue = normalizeReviewEntries(Array.isArray(entries) ? entries : []);
  }

  isSavingReview(id) {
    return this.reviewSavingIds.includes(id);
  }

  loadNameConfig() {
    return nameConfigService.loadNameConfig()
      .then((config) => {
        this.setConfig(config);
        this.dataLoaded = true;
      })
      .catch((reason) => {
        this.dataLoaded = true;
        console.error(reason);
      });
  }

  loadReviewQueue() {
    return nameConfigService.loadReviewQueue()
      .then((entries) => {
        this.setReviewQueue(entries);
        this.reviewLoaded = true;
      })
      .catch((reason) => {
        this.reviewLoaded = true;
        console.error(reason);
      });
  }

  saveNameConfig() {
    const uid = this.rootStore?.authState?.user?.uid || null;
    this.saving = true;
    return nameConfigService.saveNameConfig(uid, this.activeConfig)
      .then(() => {
        return nameConfigService.loadNameConfig().then((config) => {
          this.setConfig(config);
        });
      })
      .finally(() => {
        this.saving = false;
      });
  }

  updateRule(key, value) {
    this.config = {
      lc_name_cfg: {
        ...this.activeConfig,
        rules: {
          ...this.activeConfig.rules,
          [key]: value,
        },
      },
    };
  }

  setEnabled(value) {
    this.config = {
      lc_name_cfg: {
        ...this.activeConfig,
        enabled: !!value,
      },
    };
  }

  replaceDictionary(path, items) {
    const current = this.activeConfig;
    const nextItems = items.reduce((acc, item) => {
      const normalized = normalizeLookup(item);
      if (normalized) {
        acc[normalized] = true;
      }
      return acc;
    }, {});
    this.config = {
      lc_name_cfg: {
        ...current,
        dictionaries: {
          ...current.dictionaries,
          [path.group]: {
            ...current.dictionaries[path.group],
            [path.key]: nextItems,
          },
        },
      },
    };
  }

  addDictionaryItem(path, item) {
    const normalized = normalizeLookup(item);
    if (!normalized) {
      return false;
    }
    const currentItems = this.activeConfig?.dictionaries?.[path.group]?.[path.key] || {};
    if (currentItems[normalized]) {
      return false;
    }
    this.config = {
      lc_name_cfg: {
        ...this.activeConfig,
        dictionaries: {
          ...this.activeConfig.dictionaries,
          [path.group]: {
            ...this.activeConfig.dictionaries[path.group],
            [path.key]: {
              ...currentItems,
              [normalized]: true,
            },
          },
        },
      },
    };
    return true;
  }

  removeDictionaryItem(path, item) {
    const normalized = normalizeLookup(item);
    if (!normalized) {
      return false;
    }
    const currentItems = {...(this.activeConfig?.dictionaries?.[path.group]?.[path.key] || {})};
    if (!currentItems[normalized]) {
      return false;
    }
    delete currentItems[normalized];
    this.config = {
      lc_name_cfg: {
        ...this.activeConfig,
        dictionaries: {
          ...this.activeConfig.dictionaries,
          [path.group]: {
            ...this.activeConfig.dictionaries[path.group],
            [path.key]: currentItems,
          },
        },
      },
    };
    return true;
  }

  getDictionaryList(group, key) {
    return Object.keys(this.activeConfig?.dictionaries?.[group]?.[key] || {}).sort((a, b) => a.localeCompare(b));
  }

  applyReviewedTokensToDictionary(targetGroup, tokens = []) {
    if (!Array.isArray(tokens) || tokens.length === 0) {
      return [];
    }
    const appliedTo = [];
    const [firstToken, ...restTokens] = tokens;
    const currentConfig = clone(this.activeConfig);

    if (firstToken) {
      currentConfig.dictionaries[targetGroup].givenNames[normalizeLookup(firstToken)] = true;
      appliedTo.push(`${targetGroup}.givenNames:${normalizeLookup(firstToken)}`);
    }

    restTokens.forEach((token) => {
      const normalized = normalizeLookup(token);
      if (!normalized) {
        return;
      }
      currentConfig.dictionaries[targetGroup].surnames[normalized] = true;
      appliedTo.push(`${targetGroup}.surnames:${normalized}`);
    });

    this.setConfig({lc_name_cfg: currentConfig});
    return appliedTo;
  }

  updateLocalReviewEntry(entryId, patch) {
    this.reviewQueue = this.reviewQueue.map((entry) => entry.id === entryId ? {...entry, ...patch} : entry);
  }

  async reviewEntry(entryId, decision) {
    const entry = this.reviewQueue.find((item) => item.id === entryId);
    if (!entry) {
      return;
    }
    const uid = this.rootStore?.authState?.user?.uid || null;
    const now = new Date().toISOString();
    const normalizedDecision = decision === "approved_non_lt" ? "approved_non_lt" : decision === "ignored" ? "ignored" : "approved_lt";
    const targetGroup = normalizedDecision === "approved_non_lt" ? "nonLt" : "lt";
    const tokens = normalizeTokenList(entry.text, entry.tokens);
    const appliedTo = normalizedDecision === "ignored" ? [] : this.applyReviewedTokensToDictionary(targetGroup, tokens);
    const reviewPatch = {
      currentDecision: normalizedDecision,
      reviewed: true,
      reviewedAt: now,
      reviewedBy: uid,
      review: {
        status: normalizedDecision,
        reviewedAt: now,
        reviewedBy: uid,
        appliedTo,
      },
      updatedAt: now,
    };

    this.reviewSavingIds = [...this.reviewSavingIds, entryId];
    try {
      if (normalizedDecision !== "ignored") {
        await this.saveNameConfig();
      }
      await nameConfigService.updateReviewEntry(entryId, reviewPatch);
      this.updateLocalReviewEntry(entryId, reviewPatch);
    } catch (reason) {
      console.error(reason);
      throw reason;
    } finally {
      this.reviewSavingIds = this.reviewSavingIds.filter((id) => id !== entryId);
    }
  }
}
